<template>
  <form
    :class="b(modifiers)"
    @submit.prevent
  >
    <h2 :class="b('title', { number: true })">
      {{ $t('c-electronic-instruction-search.searchByRefTitle') }}
    </h2>
    <fieldset :class="b('search-by-number')">
      <e-search
        v-model="internalRefQuery"
        :root-class="b('search')"
        :label="$t('c-electronic-instruction-search.refSearchFieldLabel')"
        :icon="searchRefIcon"
        :state="showNumberValidationError || $slots.default ? 'error' : 'default'"
        name="search-field"
        height="400"
        force-mobile-submit
        @update:model-value="onInput"
        @reset="onReset"
        @enter="onRefSubmit"
      >
        <slot :search-url="searchUrl">
          <i18n-t
            v-if="showNumberValidationError"
            keypath="c-electronic-instruction-search.invalidReferenceNumberError"
            scope="global"
          >
            <template #searchUrl>
              <a :href="searchUrl">
                {{ $t('c-electronic-instruction-search.searchUrlLabel') }}
              </a>
            </template>
          </i18n-t>
        </slot>
      </e-search>
      <i18n-t
        :class="b('text')"
        keypath="c-electronic-instruction-search.referenceNumberInfo"
        tag="div"
        scope="global"
      >
        <template #tooltip>
          <c-tooltip
            :popper-options="{ placement: 'right' }"
            root-element="span"
          >
            {{ $t('c-electronic-instruction-search.referenceNumberTooltipLabel') }}

            <template #tooltip>
              <img
                :class="b('reference-example-image')"
                :src="referenceExampleUrl"
                :alt="$t('c-electronic-instruction-search.referenceNumberExampleAlt')"
                loading="lazy"
                decoding="async"
              />
            </template>
          </c-tooltip>
        </template>
      </i18n-t>
    </fieldset>
    <div :class="b('separator')">
      {{ $t('c-electronic-instruction-search.separatorLabel') }}
    </div>
    <h2 :class="b('title', { brand: true })">
      {{ $t('c-electronic-instruction-search.searchByBrandTitle') }}
    </h2>
    <fieldset :class="b('search-by-brand')">
      <e-search
        v-model="internalTermQuery"
        ref="brandInput"
        :root-class="b('search')"
        :label="$t('c-electronic-instruction-search.brandSearchFieldLabel')"
        :icon="searchTermIcon"
        :state="$slots.brand ? 'error' : 'default'"
        :suggestions="brandSuggestions"
        name="search-field"
        height="400"
        force-mobile-submit
        @update:model-value="onBrandInput"
        @reset="onReset"
        @enter="onBrandSubmit"
        @focus="onBrandFocus"
        @suggestion-clicked="onBrandSuggestionClick"
      >
        <slot
          :search-url="searchUrl"
          name="brand"
        ></slot>
      </e-search>
      <div :class="b('text')">
        {{ $t('c-electronic-instruction-search.searchByBrandHint') }}
      </div>
    </fieldset>
  </form>
</template>

<script>
  import { URL_PARAM_MAP, VALID_REFERENCE_NUMBER_REGEX } from '@/setup/globals';
  import useSessionStore from '@/stores/session';
  import eSearch from '@/elements/e-search.vue';
  import api from '@/helpers/api.js';
  import themes from '@/mixins/themes.js';

  /**
   * Renders an eIfU search banner.
   */
  export default {
    name: 'c-electronic-instruction-search',

    components: {
      eSearch,
    },
    mixins: [themes],

    props: {
      /**
       * Handles the search term of the number search field.
       */
      refQuery: {
        type: String,
        default: '',
      },

      /**
       * Handles the search term of the name search field.
       */
      termQuery: {
        type: String,
        default: '',
      },

      /**
       * Expects an url to display the reference number example tooltip.
       */
      referenceExampleUrl: {
        type: String,
        required: true,
      },
    },

    emits: ['error', 'submit', 'reset', 'update:termQuery', 'update:refQuery'],

    setup() {
      return {
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        /**
         * @type {String} Holds the internal search query for the number field.
         */
        internalRefQuery: '',

        /**
         * @type {Boolean} Flag that determines the visibility of form errors.
         */
        showNumberValidationError: false,

        /**
         * @type {Number} Counts the number of submits, that have been rejected because of an invalid ref number.
         */
        invalidSubmitCounter: 0,

        /**
         * @type {Array} List of strings for the brand suggestions.
         */
        brandSuggestions: [],
      };
    },

    computed: {
      /**
       * Syncs the brand query with the parent trough props.
       */
      internalTermQuery: {
        get() {
          return this.termQuery;
        },
        set(value) {
          if (!value) {
            this.onReset();
          }

          this.$emit('update:termQuery', value);
        },
      },

      /**
       * Checks if the current internalRefQuery is a valid reference number.
       *
       * @returns {Boolean}
       */
      isValidReferenceNumber() {
        return VALID_REFERENCE_NUMBER_REGEX.test(this.internalRefQuery || '');
      },

      /**
       * Returns a URL to trigger a global search for the current query.
       *
       * @returns {String}
       */
      searchUrl() {
        return `${this.sessionStore.urls.searchResultPage}?query=${this.internalRefQuery}`;
      },

      /**
       * Returns a definition of BEM modifiers.
       *
       * @returns {Object}
       */
      modifiers() {
        const { theme } = this;

        return {
          theme,
        };
      },

      /**
       * Returns the action icon that should be shown in the ref search field.
       *
       * @returns {String}
       */
      searchRefIcon() {
        return !this.$viewport.isLg || !this.internalRefQuery ? 'i-search' : 'i-close';
      },

      /**
       * Returns the action icon that should be shown in the term search field.
       *
       * @returns {String}
       */
      searchTermIcon() {
        return !this.$viewport.isLg || !this.internalTermQuery ? 'i-search' : 'i-close';
      },
    },
    watch: {
      /**
       * Updates the internal query, if the related prop changes.
       *
       * @param {String} value - The new query value.
       */
      refQuery(value) {
        this.internalRefQuery = value || '';
      },

      /**
       * Watches the internal query to auto-emit to the parent if the query is valid.
       *
       * @param {String} value - The current query value.
       */
      internalRefQuery(value) {
        if (!value) {
          this.emitRefQuery('');

          this.onReset();
        } else if (this.isValidReferenceNumber) {
          this.showNumberValidationError = false;
          this.invalidSubmitCounter = 0;

          this.updateUrl();

          this.emitRefQuery(value);
        }
      },

      /**
       * Watches the invalid submit counter to react on too many invalid tries.
       */
      invalidSubmitCounter() {
        if (this.invalidSubmitCounter > 2) {
          this.invalidSubmitCounter = 0;

          /**
           * @event error - Informs the parent, that the search resulted in an error multiple times.
           */
          this.$emit('error');
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      const search = new URLSearchParams(window.location.search);
      const refQuery = search.get(URL_PARAM_MAP.refNumber);

      if (refQuery) {
        this.internalRefQuery = refQuery;
        // NOTE: defer triggering the submit because the parent has not yet updated its query/brand variable via the two way .sync binding
        this.$nextTick(this.onRefSubmit);
      }

      document.addEventListener('pointerdown', this.onPointerDown);

      this.updateUrlParameters(search);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      document.removeEventListener('pointerdown', this.onPointerDown);
    },
    // unmounted() {},

    methods: {
      /**
       * Check clicks on the document to blur the brand search suggestions.
       *
       * @param {Event} event - The DOM event.
       */
      onPointerDown(event) {
        const focus = this.$refs.brandInput?.hasFocus || document.activeElement === this.$refs.brandInput?.$refs?.input;

        if (focus && !this.$refs.brandInput.$el.contains(event.target)) {
          this.onBrandBlur();
        }
      },

      /**
       * Updates the refQuery prop with the given value.
       *
       * @param {String} value - The new prop value.
       */
      emitRefQuery(value) {
        this.$emit('update:refQuery', value);
      },

      /**
       * Removes empty values and updates the url.
       *
       * @param {URLSearchParams} urlSearch - An URLSearchParams object.
       */
      updateUrlParameters(urlSearch) {
        if (!urlSearch.get(URL_PARAM_MAP.refNumber)) {
          urlSearch.delete(URL_PARAM_MAP.refNumber);
        }

        if (!urlSearch.get(URL_PARAM_MAP.eifuProductSearch)) {
          urlSearch.delete(URL_PARAM_MAP.eifuProductSearch);
        }

        if (urlSearch.toString()) {
          window.history.replaceState(null, null, `?${urlSearch.toString()}`);
        }
      },

      /**
       * Stores the current search query in the URL.
       */
      updateUrl() {
        const search = new URLSearchParams();

        if (this.internalRefQuery) {
          search.set(URL_PARAM_MAP.refNumber, this.internalRefQuery);
        }

        if (this.internalTermQuery) {
          search.set(URL_PARAM_MAP.eifuProductSearch, this.internalTermQuery);
        }

        search.delete(URL_PARAM_MAP.documentId);

        this.updateUrlParameters(search);
      },

      /**
       * Updates the brand suggestions.
       *
       * @param {String} value - The query value.
       */
      updateBrandSuggestions(value) {
        if (this.internalRefQuery) {
          // Prevents excessive URL updates.
          this.internalRefQuery = '';
        }

        if (!value || value.length < 3) {
          this.brandSuggestions = [];
        } else {
          const encodedQuery = encodeURIComponent(value);

          api.get(api.getUrl('eifu-brand-suggestions', { query: encodedQuery })).then((response) => {
            const { data } = response.data;

            this.brandSuggestions = data.map((item) => ({
              id: item.id,
              value: item.label,
            }));
          });
        }
      },

      /**
       * Emits a submit event to the parent.
       *
       * @param {Boolean} [error = false] - Defines if the current query is valid or not.
       * @param {Boolean} [suggestion = false] - Defines if the current query results from clicking a suggestion.
       */
      emitSubmit(error = false, suggestion = false) {
        this.$emit('submit', error, suggestion);
      },

      /**
       * Handles the click event on a brand suggestion item.
       *
       * @param {Object} suggestion - The suggestion object of the clicked item.
       */
      onBrandSuggestionClick(suggestion) {
        this.internalTermQuery = suggestion.value;
        this.emitSubmit(false, true);
        this.updateBrandSuggestions(null);

        this.$nextTick(() => {
          // Wait for props to be updated.
          this.updateUrl();
        });
      },

      /**
       * Event listener for the ref search field.
       */
      onInput() {
        if (this.internalTermQuery) {
          // Prevents excessive URL updates.
          this.internalTermQuery = '';
        }
      },

      /**
       * Focus event listener for the brand search field.
       *
       * @param {String} value - The current input value.
       */
      onBrandFocus(value) {
        this.updateBrandSuggestions(value);
      },

      /**
       * Blur event listener for the brand search field.
       */
      onBrandBlur() {
        this.updateBrandSuggestions(null);
      },

      /**
       * Event listener for the brand search field.
       *
       * @param {String} value - The current input value.
       */
      onBrandInput(value) {
        if (this.internalRefQuery) {
          // Prevents excessive URL updates.
          this.internalRefQuery = '';
        }

        this.updateBrandSuggestions(value);
      },

      /**
       * Handles the submit event for the search field.
       */
      onRefSubmit() {
        this.showNumberValidationError = !!(this.internalRefQuery && !this.isValidReferenceNumber);

        if (this.isValidReferenceNumber) {
          this.emitSubmit();

          this.updateUrl();
        } else {
          this.emitSubmit(true);

          this.invalidSubmitCounter += 1;
        }
      },

      /**
       * Handles the submit event for the name search.
       */
      onBrandSubmit() {
        this.emitSubmit();
        this.updateUrl();
        this.updateBrandSuggestions(null);
      },

      /**
       * Handles the reset event of the search field.
       */
      onReset() {
        this.showNumberValidationError = false;

        /**
         * @event reset - Forwards the search reset to the parent component.
         */
        this.$emit('reset');

        this.$nextTick(() => {
          // Wait for props to be updated.
          this.updateUrl();
        });
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/v2/variables' as v2_variables;

  .c-electronic-instruction-search {
    $this: &;
    $grid-area--number-title: number-title;
    $grid-area--number: number;
    $grid-area--separator: separator;
    $grid-area--brand-title: brand-title;
    $grid-area--brand: brand;

    @include mixins.font(variables.$font-size--16);

    display: grid;
    grid-template-areas:
      '#{$grid-area--number-title}'
      '#{$grid-area--number}'
      '#{$grid-area--separator}'
      '#{$grid-area--brand-title}'
      '#{$grid-area--brand}';
    grid-template-columns: 1fr;
    gap: functions.rem(variables.$spacing--20 0);

    @include mixins.media(sm) {
      grid-template-areas:
        '#{$grid-area--number-title} . #{$grid-area--brand-title}'
        '#{$grid-area--number} #{$grid-area--separator} #{$grid-area--brand}';
      grid-template-columns: 1fr auto 1fr;
      gap: functions.rem(0 variables.$spacing--25);
    }

    @include mixins.media(md) {
      gap: functions.rem(0 140px);
    }

    @include mixins.media(lg) {
      @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
    }

    &--theme-default {
      #{$this}__text {
        .c-tooltip {
          color: v2_variables.$color-primary--2;
          font-weight: variables.$font-weight--light;

          &:hover {
            color: v2_variables.$color-sky-700;
          }
        }
      }
    }

    &__title {
      margin-bottom: functions.rem(variables.$spacing--30);

      &--brand {
        grid-area: $grid-area--brand-title;
      }

      &--number {
        grid-area: $grid-area--number-title;
      }
    }

    &__search-by-number {
      grid-area: $grid-area--number;
    }

    &__separator {
      display: flex;
      grid-area: $grid-area--separator;
      gap: functions.rem(variables.$spacing--5 variables.$spacing--15);
      flex-direction: row;
      justify-content: stretch;
      align-items: center;

      @include mixins.media(sm) {
        flex-direction: column;
      }

      &::before,
      &::after {
        content: '';
        flex: 1 1 auto;
        border-bottom: 1px solid variables.$color-grayscale--500;

        @include mixins.media(sm) {
          border-bottom: 0;
          border-left: 1px solid variables.$color-grayscale--500;
        }
      }
    }

    &__search-by-brand {
      grid-area: $grid-area--brand;
    }

    &__search {
      margin-bottom: functions.rem(variables.$spacing--30);
    }

    &__text {
      margin-top: functions.rem(variables.$spacing--30);

      .c-tooltip {
        font-weight: variables.$font-weight--semi-bold;
        text-decoration: underline;
      }
    }

    &__reference-example-image {
      max-width: 80vw;
      height: auto;

      @include mixins.media(sm) {
        max-width: functions.rem(400px);
      }
    }
  }
</style>
